import { jsx, jsxs } from "react/jsx-runtime";
import { useReducer, useRef, useEffect, useCallback, useMemo } from "react";
import { useLayer, mergeRefs } from "react-laag";
import { useKeyDown } from "./useKeyDown.js";
import { useId } from "./useId.js";
import { Arrow } from "./Arrow.js";
import { Container } from "./Container.js";
import { Item } from "./Item4.js";
import { SectionTitle } from "./SectionTitle.js";
import { DropdownContext } from "./context3.js";
import { getUseLayerOptions } from "./getUseLayerOptions.js";
import { reducer, initialState } from "./state.js";
import { SelectButtonText } from "./SelectButtonText.js";
import { Divider } from "./Divider.js";
import { SearchInput } from "./SearchInput.js";
import { Text } from "./Text2.js";
import { useScrollHighlightedIntoView } from "./useScrollHighlightedIntoView.js";
import { ListItem } from "./ListItem2.js";
import { isInShadow, getFirstShadowElement } from "./shadowdom2.js";
import { useOffset, OffsetContext } from "./OffsetContext.js";
import { fixLayerProps } from "./fixLayerProps.js";
function Dropdown({
  children,
  trigger,
  position = "bottom-left",
  isClosedOnScroll = false,
  isKeepingFocusOnTrigger = false,
  containerRef,
  onOpen,
  onClose,
  minWidth,
  role,
  ...props
}) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const initialRender = useRef(true);
  const triggerRef = useRef();
  const menuId = useId("dropdown-menu");
  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      return;
    }
    if (state.isOpen) {
      dispatch({
        type: "highlight-current-item"
      });
      onOpen == null ? void 0 : onOpen();
    } else {
      onClose == null ? void 0 : onClose();
    }
  }, [state.isOpen]);
  useScrollHighlightedIntoView(state.highlightedItemId, state.hasSearch);
  const closeAndFocusTrigger = useCallback(() => {
    var _a;
    if (state.isOpen) {
      dispatch({
        type: "close"
      });
      (_a = triggerRef == null ? void 0 : triggerRef.current) == null ? void 0 : _a.focus();
    }
  }, [state.isOpen]);
  useEffect(() => {
    function close() {
      closeAndFocusTrigger();
    }
    if (isClosedOnScroll && state.isOpen) {
      document.addEventListener("scroll", close);
      return () => {
        document.removeEventListener("scroll", close);
      };
    }
  }, [isClosedOnScroll, closeAndFocusTrigger, state.isOpen]);
  useKeyDown("Tab", () => {
    if (state.isOpen) {
      closeAndFocusTrigger();
    }
  });
  useKeyDown("Escape", () => {
    if (state.isOpen) {
      closeAndFocusTrigger();
    }
  });
  useKeyDown("Enter", (event) => {
    var _a;
    if (state.isOpen && state.highlightedItemId) {
      (_a = document.querySelector("#" + state.highlightedItemId)) == null ? void 0 : _a.click();
      event.preventDefault();
    }
  });
  useKeyDown("ArrowDown", (event) => {
    if (state.isOpen) {
      dispatch({
        type: "highlight-next"
      });
      event.preventDefault();
    }
  });
  useKeyDown("ArrowUp", (event) => {
    if (state.isOpen) {
      dispatch({
        type: "highlight-previous"
      });
      event.preventDefault();
    }
  });
  const contextValue = useMemo(() => {
    return {
      dispatch,
      close: closeAndFocusTrigger,
      highlightedItemId: state.highlightedItemId,
      hasSearch: state.hasSearch,
      isKeepingFocusOnTrigger,
      menuId
    };
  }, [closeAndFocusTrigger, isKeepingFocusOnTrigger, state.hasSearch, state.highlightedItemId, menuId]);
  const triggerOffset = useOffset();
  const {
    renderLayer,
    triggerProps: {
      ref: triggerLayerRef
    },
    layerProps,
    triggerBounds
  } = useLayer({
    isOpen: state.isOpen,
    /**
     * Until https://github.com/everweij/react-laag/issues/84 is fixed,
     * we can't use the onOutsideClick option in the Shadow DOM.
     */
    onOutsideClick: isInShadow(triggerRef.current) ? void 0 : () => dispatch({
      type: "close"
    }),
    onDisappear: (type) => {
      if (type === "full") {
        dispatch({
          type: "close"
        });
      }
    },
    triggerOffset,
    containerOffset: 16,
    auto: true,
    container: getFirstShadowElement(triggerRef.current),
    ...getUseLayerOptions(position)
  });
  fixLayerProps(layerProps);
  const containerWidth = position === "bottom" ? {
    width: triggerBounds == null ? void 0 : triggerBounds.width,
    minWidth: "initial",
    maxWidth: "initial"
  } : {};
  function onTriggerKeyDown(event) {
    if (event.key === " " || event.key === "Enter") {
      event.preventDefault();
      dispatch({
        type: "toggle-from-keyboard"
      });
    }
  }
  return /* @__PURE__ */ jsx(DropdownContext.Provider, {
    value: contextValue,
    children: /* @__PURE__ */ jsxs("div", {
      ...props,
      children: [trigger({
        isOpen: state.isOpen,
        onClick: () => {
          dispatch({
            type: "toggle"
          });
        },
        "aria-haspopup": true,
        "aria-expanded": state.isOpen,
        "aria-controls": menuId,
        "aria-activedescendant": state.highlightedItemId,
        onKeyDown: onTriggerKeyDown,
        ref: mergeRefs(triggerLayerRef, triggerRef)
      }, {
        open: () => {
          dispatch({
            type: "open"
          });
        },
        close: () => {
          dispatch({
            type: "close"
          });
        }
      }), state.isOpen && renderLayer(/* @__PURE__ */ jsx(Container, {
        id: menuId,
        ...layerProps,
        ref: mergeRefs(layerProps.ref, containerRef),
        style: {
          ...layerProps.style,
          ...containerWidth
        },
        minWidth,
        role,
        children
      }))]
    })
  });
}
Dropdown.Divider = Divider;
Dropdown.Item = Item;
Dropdown.ListItem = ListItem;
Dropdown.SectionTitle = SectionTitle;
Dropdown.Arrow = Arrow;
Dropdown.SelectButtonText = SelectButtonText;
Dropdown.SearchInput = SearchInput;
Dropdown.Text = Text;
Dropdown.OffsetContext = OffsetContext;
export {
  Dropdown
};
//# sourceMappingURL=Dropdown.js.map
